import { Link } from "react-router-dom";
import { AlertTriangle, Repeat, Wrench } from "lucide-react";
import { fmtDate } from "@/lib/constants";

const REPAIR_KINDS = ["repair", "brake", "tyre"];
const YEAR = 365 * 86400000;

const recurring = (faults) => {
  const counts = {};
  faults.filter((f) => Date.now() - Date.parse(f.reported_at) < YEAR).forEach((f) => { counts[f.item_label] = (counts[f.item_label] || 0) + 1; });
  return Object.entries(counts).filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1]);
};

const FaultRow = ({ f, repeat }) => (
  <div data-testid={`history-fault-${f.id}`} className={`border-b px-5 py-3 last:border-0 ${repeat ? "border-l-4 border-l-red-500" : "border-l-4 border-l-amber-500/60"}`}>
    <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
      <AlertTriangle className="h-4 w-4 text-amber-400" /> {f.item_label}
      {repeat && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">Recurring</span>}
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${f.status === "open" ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>{f.status}</span>
    </div>
    <p className="mt-1 text-sm text-muted-foreground">{f.note}</p>
    <div className="mt-1 text-[11px] text-muted-foreground">{fmtDate(f.reported_at)} · {f.reported_by} · {f.stage === "post" ? "post-ride" : "pre-ride"}
      {f.run_id ? <> · <Link to={`/runs/${f.run_id}`} className="text-primary hover:underline">ride</Link></> : " · bike not taken"}</div>
    {f.resolution && <p className="mt-1 text-xs text-emerald-300">Fixed: {f.resolution} ({f.closed_by})</p>}
  </div>
);

const RepairRow = ({ r }) => (
  <div data-testid={`history-repair-${r.id}`} className="border-b border-l-4 border-l-sky-500/70 px-5 py-3 last:border-0">
    <div className="flex items-center gap-2 text-sm font-semibold"><Wrench className="h-4 w-4 text-sky-300" /> {r.title}
      <span className="rounded bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-300">{r.kind}</span></div>
    {r.details && <p className="mt-1 text-sm text-muted-foreground">{r.details}</p>}
    <div className="mt-1 text-[11px] text-muted-foreground">{fmtDate(r.date)}{r.mileage ? ` · ${r.mileage} mi` : ""}{r.supplier ? ` · ${r.supplier}` : ""}</div>
  </div>
);

export const FaultHistory = ({ faults, records }) => {
  const rec = recurring(faults);
  const repeatSet = new Set(rec.map(([l]) => l));
  const items = [
    ...faults.map((f) => ({ t: f.reported_at, el: <FaultRow key={f.id} f={f} repeat={repeatSet.has(f.item_label)} /> })),
    ...records.filter((r) => REPAIR_KINDS.includes(r.kind)).map((r) => ({ t: r.date, el: <RepairRow key={r.id} r={r} /> })),
  ].sort((a, b) => (b.t || "").localeCompare(a.t || ""));
  return (
    <section className="space-y-3" data-testid="fault-history">
      <h2 className="text-lg font-bold">Fault &amp; repair history</h2>
      {rec.length > 0 && (
        <div data-testid="recurring-faults" className="rounded-2xl border-2 border-red-500/60 bg-red-500/10 p-4">
          <div className="flex items-center gap-2 text-sm font-bold text-red-300"><Repeat className="h-4 w-4" /> Recurring problems (last 12 months)</div>
          <div className="mt-2 flex flex-wrap gap-2">{rec.map(([l, n]) => <span key={l} className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white">{l} × {n}</span>)}</div>
        </div>
      )}
      <div className="overflow-hidden rounded-2xl border bg-card">
        {items.length ? items.map((i) => i.el) : <p data-testid="fault-history-empty" className="px-5 py-8 text-sm text-muted-foreground">No faults or repairs recorded for this vehicle.</p>}
      </div>
    </section>
  );
};
